import * as React from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { useGetEverguyardSettingLazyQuery, useGetPlaceCodeNameLazyQuery, useGetPlaceCodesAndSortsQuery, useGetPlaceSortNameLazyQuery, useUpdatePlaceCodesAndSortsMutation } from '../generated/graphql';
import { LineSelect, HatchSelect } from '../components/ImageSelect';
import { Loading } from '../Loading';

import './PlaceCreateCodeAndSortPage.css';
import { FormActions } from '../components/FormActions';
import { Input } from '../components/Input';
import { SectionHeader } from '../components/SectionHeader';
import { ToastContext } from '../components/Toast';
import { ColorPaletteSelect } from '../components/ColorPaletteSelect';
import {
  DEFAULT_AREA_DISPLAY_UNIT,
  formatEverguardThresholdLabel,
} from '../utils/PlaceCodeDisplayUnits';

type IPlaceCreateCodeAndSortPageProps = RouteComponentProps<{
    placeId: string,
    codeId: string,
    sortId: string,
    gteEverguardThreshold: string,
}> & {};

export const PlaceCreateCodeAndSortPage: React.FC<IPlaceCreateCodeAndSortPageProps> = function (props) {
  const placeId = parseInt(props.match.params.placeId);
  const placeCodeId = props.match.params.codeId === 'none' ? null : parseInt(props.match.params.codeId);
  const placeSortId = props.match.params.sortId === 'none' ? null : parseInt(props.match.params.sortId);
  const gteEverguardThreshold = props.match.params.gteEverguardThreshold === 'true' ? true : false;
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const listPath = `/places/${placeId}/codes_and_sorts`;

  const [prefix, setPrefix] = React.useState('');
  const [color, setColor] = React.useState('#FFFFFF');
  const [lineType, setLineType] = React.useState(0);
  const [hatchType, setHatchType] = React.useState(0);
  // 保存しても有効・無効が変わらないよう、既存の設定の値を保持して送る。設定がまだない組み合わせは有効として扱う
  const [disabled, setDisabled] = React.useState(false);
  const [getPlaceCodeName, getPlaceCodeNameResult] = useGetPlaceCodeNameLazyQuery();
  const [getPlaceSortName, getPlaceSortNameResult] = useGetPlaceSortNameLazyQuery();
  const [getEverguardSetting, getEverguardSettingResult] = useGetEverguyardSettingLazyQuery();
  const [updatePlaceCodeSortConfig, updatePlaceCodeSortConfigResult] = useUpdatePlaceCodesAndSortsMutation({
    onCompleted() {
      toastContext.showToast('保存しました', 'success');
      history.push(listPath);
    },
    onError() {
      toastContext.showToast('保存できませんでした。時間をおいて再実行してください。', 'error');
    },
  });
  const getPlaceCodeAndSortResult = useGetPlaceCodesAndSortsQuery({
    variables: { placeId },
    onCompleted(data) {
      const config = data.getPlace.code_sort_configs.find(config =>
        config.code_id === placeCodeId && config.sort_id === placeSortId && config.gte_everguard_threshold === gteEverguardThreshold);

      if (!config) {
        return;
      }

      setPrefix(config.prefix);
      setColor(config.color);
      setHatchType(config.hatch_type);
      setLineType(config.line_type);
      setDisabled(config.disabled);
    }
  });

  if (placeCodeId) {
    if (!getPlaceCodeNameResult.called) {
      getPlaceCodeName({ variables: { placeCodeId } });
    }
    if (!getEverguardSettingResult.called) {
      getEverguardSetting({ variables: { placeCodeId } });
    }
  }
  if (!getPlaceSortNameResult.called && placeSortId) {
    getPlaceSortName({ variables: { placeSortId } });
  }

  if ((getPlaceCodeNameResult.called && (getPlaceCodeNameResult.loading || !getPlaceCodeNameResult.data)) ||
      (getPlaceSortNameResult.called && (getPlaceSortNameResult.loading || !getPlaceSortNameResult.data)) ||
      (getPlaceCodeAndSortResult.loading && !getPlaceCodeAndSortResult.data)) {
    return <Loading />;
  }

  let codeName;
  if (getPlaceCodeNameResult.data) {
    codeName = getPlaceCodeNameResult.data.getPlaceCode.name;
  } else {
    codeName = '指定なし';
  }
  const areaDisplayUnit = getPlaceCodeNameResult.data?.getPlaceCode.area_display_unit ?? DEFAULT_AREA_DISPLAY_UNIT;
  if (getEverguardSettingResult.data?.getEverguardSetting) {
    if (gteEverguardThreshold) {
      codeName = `${codeName}（${formatEverguardThresholdLabel(getEverguardSettingResult.data.getEverguardSetting.everguard_threshold, areaDisplayUnit)} 以上）`;
    } else {
      codeName = `${codeName}（${formatEverguardThresholdLabel(getEverguardSettingResult.data.getEverguardSetting.everguard_threshold, areaDisplayUnit)} 未満）`;
    }
  }

  let sortName;
  if (getPlaceSortNameResult.data) {
    sortName = getPlaceSortNameResult.data.getPlaceSort.name;
  } else {
    sortName = '指定なし';
  }

  return (
    <div className="PlaceCreateCodeAndSortPage-container">
      <Loading open={updatePlaceCodeSortConfigResult.loading} />
      <SectionHeader title="組み合わせの編集" />
      <div className="PlaceCreateCodeAndSortPage-input_container">
        <label className="App-label">補修コード名</label>
        <div>
          <span>{codeName}</span>
        </div>
      </div>
      <div className="PlaceCreateCodeAndSortPage-input_container">
        <label className="App-label">補修分類名</label>
        <div>
          <span>{sortName}</span>
        </div>
      </div>
      <div className="PlaceCreateCodeAndSortPage-input_container">
        <label className="App-label">前置文字列</label>
        <div>
          <Input
            type="string"
            onChange={(event) => {
              const prefix = event.target.value;
              setPrefix(prefix);
            }}
            value={prefix}
          />
        </div>
      </div>
      <div className="PlaceCreateCodeAndSortPage-input_container">
        <label className="App-label">画層の色</label>
        <div className="PlaceCreateCodeAndSortPage-color_container">
          <div>
            <ColorPaletteSelect
              color={color}
              onChange={setColor}
            />
          </div>
        </div>
      </div>
      <div className="PlaceCreateCodeAndSortPage-input_container">
        <label className="App-label">線種</label>
        <div>
          <LineSelect
            value={lineType}
            onChange={(type) => {
              setLineType(type);
            }}
            containerClassName="PlaceCreateCodeAndSortPage-image_select"
            optionClassName="PlaceCreateCodeAndSortPage-image_option"
            />
        </div>
      </div>
      <div className="PlaceCreateCodeAndSortPage-input_container">
        <label className="App-label">テクスチャ</label>
        <div>
          <HatchSelect
            value={hatchType}
            onChange={(type) => {
              setHatchType(type);
            }}
            containerClassName="PlaceCreateCodeAndSortPage-image_select"
            optionClassName="PlaceCreateCodeAndSortPage-image_option"
            />
        </div>
      </div>
      <FormActions
        submitLabel="保存"
        submitDisabled={updatePlaceCodeSortConfigResult.loading}
        onCancel={() => history.push(listPath)}
        onSubmit={() => {
          updatePlaceCodeSortConfig({
            variables: {
              input: {
                place_id: placeId,
                code_id: placeCodeId,
                sort_id: placeSortId,
                prefix: prefix,
                color: color,
                line_type: lineType,
                hatch_type: hatchType,
                disabled: disabled,
                place_code_everguard_id: getEverguardSettingResult.data?.getEverguardSetting?.id ?? undefined,
                gte_everguard_threshold: gteEverguardThreshold
              }
            }
          });
        }}
      />
    </div>
  );
};
